import React from 'react'
import { Document, Page, Text, View, Image, StyleSheet, Font, PDFViewer, PDFDownloadLink } from '@react-pdf/renderer'
import agreementData from '../json/rentagreementdata.json'

// ─────────────────────────────────────────────────────────────────
// LETTERHEAD ARTWORK
// ─────────────────────────────────────────────────────────────────
const LETTERHEAD_FIRST = '/assets/letterhead-first.png'

// ─────────────────────────────────────────────────────────────────
// FONT REGISTRATION
// The built-in "Helvetica" PDF standard font only supports WinAnsi
// encoding, which does NOT include the ₹ (U+20B9) glyph — that's why
// it silently disappears from the rendered PDF. Noto Sans includes
// the Rupee sign, so we register it here and use it wherever we
// need to render ₹.
//
// NOTE: If your build environment has no internet access at PDF
// render time (e.g. an offline server or CI pipeline), download
// NotoSans-Regular.ttf and NotoSans-Bold.ttf yourself, place them in
// your project (e.g. /assets/fonts/), and point `src` at the local
// path instead of the CDN URL below.
// ─────────────────────────────────────────────────────────────────
Font.register({
  family: 'NotoSans',
  fonts: [
    { src: 'https://cdn.jsdelivr.net/gh/googlefonts/noto-fonts/hinted/ttf/NotoSans/NotoSans-Regular.ttf', fontWeight: 'normal' },
    { src: 'https://cdn.jsdelivr.net/gh/googlefonts/noto-fonts/hinted/ttf/NotoSans/NotoSans-Bold.ttf', fontWeight: 'bold' },
  ],
})

// ═════════════════════════════════════════════════════════════════
// PDF STYLES
// ═════════════════════════════════════════════════════════════════

const PAGE_PADDING_TOP    = 140
const PAGE_PADDING_BOTTOM = 85
const PAGE_PADDING_SIDE   = 48

// ─────────────────────────────────────────────────────────────────
// HEADER LAYOUT (matches the icon/divider positions already baked
// into letterhead-first.png). All values in points (pt), measured
// off the artwork at its native 1414×2000px size, scaled to the
// A4 page size of 595.28×841.89pt (scale factor ≈ 2.3753).
// Tweak these if you ever swap in a differently-laid-out letterhead.
// ─────────────────────────────────────────────────────────────────
const HEADER_NAME_TOP     = 62   // top of the PG name block
const HEADER_NAME_LEFT    = 48
const HEADER_NAME_WIDTH   = 240  // stays left of the vertical divider (~297pt)

const HEADER_ROW_LEFT     = 340  // just right of the phone/mail/pin icons
const HEADER_ROW_WIDTH    = 210
const HEADER_ROW1_TOP     = 70  // aligned with the phone icon
const HEADER_ROW2_TOP     = 86  // aligned with the envelope icon
const HEADER_ROW3_TOP     = 102  // aligned with the pin icon (address may wrap 2 lines)

// ─────────────────────────────────────────────────────────────────
// FOOTER LAYOUT
// Sits in the white gap above the orange/black bars that are
// already baked into the bottom of letterhead-first.png.
// ─────────────────────────────────────────────────────────────────
const FOOTER_TEXT_BOTTOM  = 46   // distance from page bottom edge

const docStyles = StyleSheet.create({
  page: {
    paddingTop: PAGE_PADDING_TOP,
    paddingBottom: PAGE_PADDING_BOTTOM,
    paddingHorizontal: PAGE_PADDING_SIDE,
    fontSize: 9.5,
    lineHeight: 1.45,
    fontFamily: 'Helvetica',
    color: '#1F2937',
  },
  background: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    zIndex: -1,
  },
  title: {
    fontSize: 13,
    fontFamily: 'Helvetica-Bold',
    textAlign: 'center',
    marginBottom: 12,
    letterSpacing: 1,
  },
  sectionHeading: {
    fontSize: 10.5,
    fontFamily: 'Helvetica-Bold',
    marginTop: 10,
    marginBottom: 4,
  },
  // ─── BOLD STYLES ──────────────────────────────────────────────
  bold: { 
    fontFamily: 'Helvetica-Bold' 
  },
  keyword: { 
    fontFamily: 'Helvetica-Bold' 
  },
  headingBold: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 10.5,
  },
  subheadingBold: {
    fontFamily: 'Helvetica-Bold',
  },
  textCenterBold: {
    textAlign: 'center',
    fontFamily: 'Helvetica-Bold',
  },
  // ──────────────────────────────────────────────────────────────
  clause: { marginBottom: 4, textAlign: 'justify' },
  bulletRow: { flexDirection: 'row', marginBottom: 3 },
  bulletDot: { width: 10 },
  bulletText: { flex: 1, textAlign: 'justify' },
  signBlockWrap: { marginTop: 18 },
  signRow: { flexDirection: 'row', justifyContent: 'space-between', marginTop: 22 },
  signCol: { width: '46%' },
  signLabel: { fontFamily: 'Helvetica-Bold', marginBottom: 4 },
  signLine: { borderBottomWidth: 1, borderBottomColor: '#374151', marginBottom: 3 },
  signSub: { fontSize: 8.5, color: '#4B5563' },
  witnessWrap: { marginTop: 22 },
  witnessRow: { flexDirection: 'row', justifyContent: 'space-between', marginTop: 6 },
  witnessCol: { width: '46%' },
  // ─── RUPEE SYMBOL STYLE ──────────────────────────────────────
  // Uses NotoSans (not Helvetica) because Helvetica's WinAnsi
  // encoding does not include the ₹ glyph.
  rupee: {
    fontFamily: 'NotoSans',
    fontWeight: 'bold',
    fontSize: 9.5,
  },
  // ─── DYNAMIC HEADER (overlaid on the letterhead artwork) ──────
  pgName: {
    position: 'absolute',
    top: HEADER_NAME_TOP,
    left: HEADER_NAME_LEFT,
    width: HEADER_NAME_WIDTH,
    fontSize: 24,
    lineHeight: 1.2,
    fontFamily: 'Helvetica-Bold',
    color: '#111827',
  },
  pgContactRow: {
    position: 'absolute',
    left: HEADER_ROW_LEFT,
    width: HEADER_ROW_WIDTH,
    fontSize: 8,
    lineHeight: 1.25,
    color: '#374151',
  },
  // ─── DYNAMIC FOOTER (overlaid on the letterhead artwork) ──────
  pgFooter: {
    position: 'absolute',
    bottom: FOOTER_TEXT_BOTTOM,
    left: 0,
    right: 0,
    textAlign: 'center',
    fontSize: 11,
    fontFamily: 'Helvetica-Bold',
    color: '#111827',
  },
})

const Section = ({ children }) => <Text style={docStyles.sectionHeading}>{children}</Text>
const Clause = ({ children }) => <Text style={docStyles.clause}>{children}</Text>

const Bullets = ({ items }) => (
  <View>
    {items.map((t, i) => (
      <View style={docStyles.bulletRow} key={i}>
        <Text style={docStyles.bulletDot}>•</Text>
        <Text style={docStyles.bulletText}>{t}</Text>
      </View>
    ))}
  </View>
)

const RuleSection = ({ number, title, intro, items }) => (
  <>
    <Section>{number}. {title}</Section>
    {intro && <Clause>{intro}</Clause>}
    <Bullets items={items} />
  </>
)

// ─── RUPEE SYMBOL COMPONENT ─────────────────────────────────────
// Using Unicode ₹ (U+20B9) rendered with NotoSans, which supports it.
const Rupee = () => <Text style={docStyles.rupee}>₹</Text>

// ═════════════════════════════════════════════════════════════════
// RENT AGREEMENT DOCUMENT - Directly uses the JSON data
// ═════════════════════════════════════════════════════════════════

export const RentAgreementDocument = ({ data } = {}) => {
  const d = data || agreementData
  const pg = d.pg
  const lo = d.landlord
  const tn = d.tenant
  const pr = d.property
  const tc = d.tenancy
  const rn = d.rent
  const dp = d.deposit
  const ut = d.utilities
  const fd = d.food
  const ru = d.rules
  const tm = d.termination

  let sectionCount = 0
  const num = () => { sectionCount += 1; return sectionCount }

  const ruleSections = [
    {
      key: 'houseRules',
      title: 'HOUSE RULES & TENANT OBLIGATIONS',
      intro: 'The Tenant agrees to abide by the following:',
      items: ru.houseRules,
    },
    {
      key: 'landlordObligations',
      title: "LANDLORD'S OBLIGATIONS",
      intro: 'The Landlord shall:',
      items: ru.landlordObligations,
    },
    {
      key: 'prohibitedActivities',
      title: 'PROHIBITED ACTIVITIES',
      intro: 'The Tenant shall not:',
      items: ru.prohibitedActivities,
    },
  ]

  return (
    <Document>
      <Page size="A4" style={docStyles.page} wrap>
        {/* Letterhead background */}
        <Image 
          src={LETTERHEAD_FIRST} 
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: 595.28,
            height: 841.89,
            zIndex: -1,
          }}
          fixed 
        />

        {/* ─── DYNAMIC HEADER: PG name + contact rows ───────────── */}
        {/* fixed = redrawn on every physical page, same as the letterhead art */}
        <Text style={docStyles.pgName} fixed>{pg.name}</Text>
        <Text style={[docStyles.pgContactRow, { top: HEADER_ROW1_TOP }]} fixed>{pg.phone}</Text>
        <Text style={[docStyles.pgContactRow, { top: HEADER_ROW2_TOP }]} fixed>{pg.email}</Text>
        <Text style={[docStyles.pgContactRow, { top: HEADER_ROW3_TOP }]} fixed>{pg.address}</Text>

        {/* ─── DYNAMIC FOOTER: "Thank you for choosing <PG name>" ─ */}
        <Text style={docStyles.pgFooter} fixed>Thank you for choosing {pg.name}</Text>

        <Text style={docStyles.title}>RENT AGREEMENT</Text>

        {/* ─── CLAUSE 1: Agreement Date ─────────────────────────── */}
        <Clause>
          This Rent Agreement is made and executed on this <Text style={docStyles.keyword}>{d.agreementDay}</Text> <Text style={docStyles.keyword}>{d.agreementDate}</Text> at <Text style={docStyles.keyword}>{d.city}</Text>.
        </Clause>

        {/* ─── BETWEEN ───────────────────────────────────────────── */}
        <Clause><Text style={docStyles.textCenterBold}>BETWEEN</Text></Clause>

        {/* ─── LANDLORD DETAILS ──────────────────────────────────── */}
        <Clause>
          <Text style={docStyles.keyword}>{lo.title} {lo.name}</Text>, S/o <Text style={docStyles.keyword}>{lo.parent}</Text>, aged <Text style={docStyles.keyword}>{lo.age}</Text> years,
          residing at <Text style={docStyles.keyword}>{lo.address}</Text>, PIN: <Text style={docStyles.keyword}>{lo.pin}</Text>,
          Mobile: <Text style={docStyles.keyword}>{lo.mobile}</Text>, Email: <Text style={docStyles.keyword}>{lo.email}</Text> (hereinafter
          referred to as the <Text style={docStyles.bold}>"Landlord"</Text> or <Text style={docStyles.bold}>"PG Owner"</Text>, which expression shall, unless repugnant to the
          context, mean and include his/her heirs, executors, administrators and assigns) of the <Text style={docStyles.bold}>ONE PART</Text>;
        </Clause>

        {/* ─── AND ────────────────────────────────────────────────── */}
        <Clause><Text style={docStyles.textCenterBold}>AND</Text></Clause>

        {/* ─── TENANT DETAILS ────────────────────────────────────── */}
        {/* S/o-D/o and PIN are only shown when present — the tenant
            self-registration form doesn't collect these yet, so we
            don't want to print "S/o " or "PIN: " followed by blanks. */}
        <Clause>
          <Text style={docStyles.keyword}>{tn.title} {tn.name}</Text>
          {tn.parent ? <>, S/o or D/o <Text style={docStyles.keyword}>{tn.parent}</Text></> : null}
          {tn.age ? <>, aged <Text style={docStyles.keyword}>{tn.age}</Text> years</> : null},{' '}
          <Text style={docStyles.keyword}>{tn.occupation}</Text>, residing at <Text style={docStyles.keyword}>{tn.address}</Text>
          {tn.pin ? <>, PIN: <Text style={docStyles.keyword}>{tn.pin}</Text></> : null},{' '}
          Mobile: <Text style={docStyles.keyword}>{tn.mobile}</Text>, Email: <Text style={docStyles.keyword}>{tn.email}</Text> (hereinafter
          referred to as the <Text style={docStyles.bold}>"Tenant"</Text>, which expression shall, unless repugnant to the context, mean and include his/her heirs,
          executors, administrators and assigns) of the <Text style={docStyles.bold}>OTHER PART</Text>.
        </Clause>

        {/* ─── WHEREAS 1: Property ───────────────────────────────── */}
        <Clause>
          <Text style={docStyles.headingBold}>WHEREAS</Text> the Landlord is the absolute owner of the property situated at <Text style={docStyles.keyword}>{pr.address}</Text>,
          PIN <Text style={docStyles.keyword}>{pr.pin}</Text> (hereinafter referred to as the "PG Premises").
        </Clause>

        {/* ─── WHEREAS 2: Room Details ─────────────────────────────
            Before a bed/room is actually allotted (e.g. at the tenant
            application stage), we don't yet know occupancy type, floor,
            or room number — so pr.roomAssigned is set to false and we
            fall back to a generic clause instead of printing blanks. */}
        {pr.roomAssigned === false ? (
          <Clause>
            <Text style={docStyles.headingBold}>WHEREAS</Text> the Tenant has approached the Landlord for paying-guest accommodation
            in <Text style={docStyles.keyword}>{pr.pgName}</Text> for residential purpose only. The specific room, floor, and occupancy
            type shall be allotted solely at the discretion of the Landlord based on availability at the time of check-in, and shall
            be recorded and confirmed separately at the time of such allotment.
          </Clause>
        ) : (
          <Clause>
            <Text style={docStyles.headingBold}>WHEREAS</Text> the Tenant has approached the Landlord for <Text style={docStyles.keyword}>{pr.roomDetails}</Text> bearing
            Room No. <Text style={docStyles.keyword}>{pr.roomNo}</Text> in <Text style={docStyles.keyword}>{pr.pgName}</Text> for residential purpose only.
          </Clause>
        )}

        {/* ─── NOW THIS AGREEMENT WITNESSETH ────────────────────── */}
        <Clause><Text style={docStyles.headingBold}>NOW THIS AGREEMENT WITNESSETH AS FOLLOWS:</Text></Clause>

        {/* ════════════════════════════════════════════════════════════
            SECTION 1: TENANCY TERM
            ════════════════════════════════════════════════════════════ */}
        <Section>{num()}. TENANCY TERM</Section>
        <Clause>{sectionCount}.1 The Landlord agrees to let and the Tenant agrees to take on rent the said room for a period of <Text style={docStyles.keyword}>{tc.durationMonths} months</Text> commencing
          from <Text style={docStyles.keyword}>{tc.startDate}</Text> and ending on <Text style={docStyles.keyword}>{tc.endDate}</Text>.</Clause>
        <Clause>{sectionCount}.2 There shall be a lock-in period of <Text style={docStyles.keyword}>{tc.lockInMonths} months</Text> from the commencement date during which neither party
          can terminate the agreement except in case of breach.</Clause>
        <Clause>{sectionCount}.3 The agreement may be renewed for a further period on mutually agreed terms by giving a written notice at least{' '}
          <Text style={docStyles.keyword}>{tc.renewalNoticeDays} days</Text> before expiry.</Clause>

        {/* ════════════════════════════════════════════════════════════
            SECTION 2: RENT & PAYMENT - Using Rupee component
            ════════════════════════════════════════════════════════════ */}
        <Section>{num()}. RENT & PAYMENT TERMS</Section>
        <Clause>{sectionCount}.1 The Tenant shall pay a monthly rent of <Rupee /><Text style={docStyles.keyword}>{rn.monthlyRent}</Text> (Rupees{' '}
          <Text style={docStyles.keyword}>{rn.monthlyRentWords}</Text> only) to the Landlord.</Clause>
        <Clause>{sectionCount}.2 The rent shall be payable on or before the <Text style={docStyles.keyword}>{rn.dueDay}</Text> day of every succeeding calendar month.</Clause>
        <Clause>{sectionCount}.3 Payment shall be made through <Text style={docStyles.keyword}>{rn.paymentMode}</Text> to the Landlord's designated account.</Clause>
        <Clause>{sectionCount}.4 A late payment penalty of <Rupee /><Text style={docStyles.keyword}>{rn.latePenalty}</Text> per day shall be charged for any delay beyond the due date.</Clause>
        <Clause>{sectionCount}.5 The rent shall be subject to an escalation of <Text style={docStyles.keyword}>{rn.escalationPercent}%</Text> after every{' '}
          <Text style={docStyles.keyword}>{rn.escalationMonths} months</Text> upon renewal.</Clause>

        {/* ════════════════════════════════════════════════════════════
            SECTION 3: SECURITY DEPOSIT - Using Rupee component
            ════════════════════════════════════════════════════════════ */}
        <Section>{num()}. SECURITY DEPOSIT</Section>
        <Clause>{sectionCount}.1 The Tenant has paid a sum of <Rupee /><Text style={docStyles.keyword}>{dp.amount}</Text> (Rupees{' '}
          <Text style={docStyles.keyword}>{dp.amountWords}</Text> only) as interest-free refundable security deposit.</Clause>
        <Clause>{sectionCount}.2 The deposit shall be refunded within <Text style={docStyles.keyword}>{dp.refundDays} days</Text> after the Tenant vacates the premises,
          subject to deduction for any damages, unpaid rent, or dues.</Clause>

        {/* ════════════════════════════════════════════════════════════
            SECTION 4: UTILITIES
            ════════════════════════════════════════════════════════════ */}
        <Section>{num()}. UTILITIES & MAINTENANCE</Section>
        <Clause>{sectionCount}.1 The following are included in the rent: <Text style={docStyles.keyword}>{ut.included}</Text></Clause>
        <Clause>{sectionCount}.2 The following shall be paid extra by the Tenant: <Text style={docStyles.keyword}>{ut.extra}</Text></Clause>
        <Clause>{sectionCount}.3 Electricity and other metered charges beyond the included limit will be calculated as per actual sub-meter reading and paid by the Tenant within 7 days of intimation.</Clause>

        {/* ════════════════════════════════════════════════════════════
            SECTION 5: FOOD
            ════════════════════════════════════════════════════════════ */}
        <Section>{num()}. FOOD / MEALS (if applicable)</Section>
        <Clause>{sectionCount}.1 The Landlord shall provide <Text style={docStyles.keyword}>{fd.included}</Text> as per the standard menu on all days except{' '}
          <Text style={docStyles.keyword}>{fd.offDay}</Text>.</Clause>
        <Clause>{sectionCount}.2 Extra meals or special requests will be charged additionally at the rate fixed by the Landlord.</Clause>

        {/* ════════════════════════════════════════════════════════════
            SECTIONS 6-8: RULES
            ════════════════════════════════════════════════════════════ */}
        {ruleSections.map(s => (
          <RuleSection key={s.key} number={num()} title={s.title} intro={s.intro} items={s.items} />
        ))}

        {/* ════════════════════════════════════════════════════════════
            SECTION 9: TERMINATION
            ════════════════════════════════════════════════════════════ */}
        <Section>{num()}. TERMINATION & NOTICE PERIOD</Section>
        <Clause>{sectionCount}.1 Either party may terminate the agreement by giving <Text style={docStyles.keyword}>{tm.noticeDays} days</Text> written notice after the lock-in period.</Clause>
        <Clause>{sectionCount}.2 In case of breach of any terms, the Landlord may terminate the agreement immediately and forfeit the security deposit.</Clause>
        <Clause>{sectionCount}.3 Upon termination, the Tenant must vacate the premises and clear all dues.</Clause>

        {/* ════════════════════════════════════════════════════════════
            SECTION 10: DEFAULT & BREACH
            ════════════════════════════════════════════════════════════ */}
        <Section>{num()}. DEFAULT & BREACH</Section>
        <Clause>In case of default in payment of rent for two consecutive months or repeated violation of house rules, the Landlord shall have the right to evict
          the Tenant as per applicable law.</Clause>

        {/* ════════════════════════════════════════════════════════════
            SECTION 11: FORCE MAJEURE
            ════════════════════════════════════════════════════════════ */}
        <Section>{num()}. FORCE MAJEURE</Section>
        <Clause>Neither party shall be liable for any delay or failure to perform obligations due to events beyond their control such as acts of God, war, riots,
          strikes, etc.</Clause>

        {/* ════════════════════════════════════════════════════════════
            SECTION 12: DISPUTE RESOLUTION
            ════════════════════════════════════════════════════════════ */}
        <Section>{num()}. DISPUTE RESOLUTION</Section>
        <Clause>Any dispute arising out of this agreement shall be subject to the exclusive jurisdiction of the courts at <Text style={docStyles.keyword}>{d.city}</Text>.
          The parties may first attempt amicable settlement.</Clause>

        {/* ════════════════════════════════════════════════════════════
            SECTION 13: GOVERNING LAW
            ════════════════════════════════════════════════════════════ */}
        <Section>{num()}. GOVERNING LAW</Section>
        <Clause>This Agreement shall be governed by the laws of India and the state of <Text style={docStyles.keyword}>{d.state}</Text>.</Clause>

        {/* ════════════════════════════════════════════════════════════
            SECTION 14: MISCELLANEOUS
            ════════════════════════════════════════════════════════════ */}
        <Section>{num()}. MISCELLANEOUS</Section>
        <Clause>{sectionCount}.1 This Agreement constitutes the entire understanding between the parties.</Clause>
        <Clause>{sectionCount}.2 Any modification must be made in writing and signed by both parties.</Clause>
        <Clause>{sectionCount}.3 Notices shall be sent to the addresses mentioned above.</Clause>

        {/* ─── IN WITNESS WHEREOF ────────────────────────────────── */}
        <Clause>IN WITNESS WHEREOF, the parties have set their hands on the day and year first above written.</Clause>

        {/* ─── SIGNATURE SECTION ────────────────────────────────── */}
        <View style={docStyles.signBlockWrap}>
          {/* Landlord & Tenant in a row */}
          <View style={docStyles.signRow}>
            <View style={docStyles.signCol}>
              <Text style={docStyles.signLabel}>Landlord</Text>
              <Text style={docStyles.signSub}>Signature:</Text>
              <View style={docStyles.signLine} />
              <Text style={docStyles.signSub}>Name: <Text style={docStyles.keyword}>{lo.title} {lo.name}</Text></Text>
              <Text style={docStyles.signSub}>Date: <Text style={docStyles.keyword}>{d.agreementDate}</Text></Text>
            </View>
            <View style={docStyles.signCol}>
              <Text style={docStyles.signLabel}>Tenant</Text>
              <Text style={docStyles.signSub}>
                Signature: <Text style={{ fontSize: 10 }}>{tn.name}</Text>
              </Text>
              <View style={docStyles.signLine} />
              <Text style={docStyles.signSub}>Name: <Text style={docStyles.keyword}>{tn.title} {tn.name}</Text></Text>
              <Text style={docStyles.signSub}>Date: <Text style={docStyles.keyword}>{d.agreementDate}</Text></Text>
            </View>
          </View>

          {/* Witnesses in a row */}
          <View style={docStyles.witnessWrap}>
            <Text style={docStyles.signLabel}>Witnesses</Text>
            <View style={docStyles.witnessRow}>
              {/* Witness 1 */}
              <View style={docStyles.witnessCol}>
                <Text style={docStyles.signSub}>Signature:</Text>
                <View style={docStyles.signLine} />
                <Text style={docStyles.signSub}>Name: ___________________</Text>
                <Text style={docStyles.signSub}>Address: ___________________</Text>
                <Text style={docStyles.signSub}>Mobile: ___________________</Text>
              </View>
              {/* Witness 2 */}
              <View style={docStyles.witnessCol}>
                <Text style={docStyles.signSub}>Signature:</Text>
                <View style={docStyles.signLine} />
                <Text style={docStyles.signSub}>Name: ___________________</Text>
                <Text style={docStyles.signSub}>Address: ___________________</Text>
                <Text style={docStyles.signSub}>Mobile: ___________________</Text>
              </View>
            </View>
          </View>
        </View>
      </Page>
    </Document>
  )
}

// ═════════════════════════════════════════════════════════════════
// VIEW / DOWNLOAD MODAL
// ═════════════════════════════════════════════════════════════════

const ViewAgreementModal = ({ fileName = 'Rent_Agreement.pdf', onClose, data }) => {
  const agData = data || agreementData
  const [isMobile, setIsMobile] = React.useState(false)

  React.useEffect(() => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera
    const mobileRegex = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i
    setIsMobile(mobileRegex.test(userAgent.toLowerCase()))
  }, [])

  const handleOpenPDF = async () => {
    const { pdf } = await import('@react-pdf/renderer')
    const blob = await pdf(<RentAgreementDocument data={agData} />).toBlob()
    const url = URL.createObjectURL(blob)
    window.open(url, '_blank')
    setTimeout(() => URL.revokeObjectURL(url), 60000)
  }

  return (
    <div className="va-overlay" onClick={onClose}>
      <div className="va-modal" onClick={e => e.stopPropagation()}>
        <div className="va-modal-head">
          <div>
            <h3 className="va-modal-title">Rent Agreement</h3>
            <p className="va-modal-sub">
              {agData.tenant.name} · Room {agData.property.roomNo || 'TBD'}
            </p>
          </div>

          <div className="va-modal-actions">
            {isMobile && (
              <button className="va-download-btn" onClick={handleOpenPDF} style={{ marginRight: '8px' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" />
                </svg>
                Open PDF
              </button>
            )}
            
            <PDFDownloadLink
              document={<RentAgreementDocument data={agData} />}
              fileName={fileName}
              className="va-download-btn"
            >
              {({ loading }) => (
                <>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" />
                  </svg>
                  {loading ? 'Preparing…' : 'Download PDF'}
                </>
              )}
            </PDFDownloadLink>

            <button className="va-modal-close" onClick={onClose} aria-label="Close">
              <svg width="16" height="16" viewBox="0 0 18 18" fill="none">
                <path d="M1 1L17 17M17 1L1 17" stroke="#374151" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>
          </div>
        </div>

        <div className="va-modal-body">
          {isMobile ? (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              backgroundColor: '#f9fafb',
              padding: '20px',
              textAlign: 'center'
            }}>
              <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="#f06e18" strokeWidth="1.5">
                <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                <path d="M14 2v6h6M12 18v-6M9 15l3-3 3 3" />
              </svg>
              <h3 style={{ marginTop: '16px', color: '#111827', fontSize: '18px' }}>
                Rent Agreement
              </h3>
              <p style={{ color: '#6b7280', maxWidth: '300px', fontSize: '14px' }}>
                Tap <strong>"Open PDF"</strong> above to view your agreement in a new tab.
              </p>
              <p style={{ color: '#9ca3af', fontSize: '12px', marginTop: '8px' }}>
                {agData.tenant.name} · Room {agData.property.roomNo || 'TBD'}
              </p>
            </div>
          ) : (
            <PDFViewer width="100%" height="100%" showToolbar={false}>
              <RentAgreementDocument data={agData} />
            </PDFViewer>
          )}
        </div>
      </div>
    </div>
  )
}

export default ViewAgreementModal